import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { registryChangeRequestAPI } from '../../services/api';
import {
  buildRegistryReviewPath,
  canOpenRegistryReviewQueue,
  canVerifyRegistryQueueDomain,
  canViewRegistryQueueDomain,
  compactRegistryUid,
  isOwnRegistryProposal,
  normalizeRegistryQueuePage,
} from './registryReviewQueueUi';
import './AdminRegistryReviewQueue.css';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending review / प्रलंबित' },
  { value: '', label: 'All statuses / सर्व स्थिती' },
  { value: 'approved', label: 'Approved / मंजूर' },
  { value: 'rejected', label: 'Rejected / नामंजूर' },
];

const STATUS_LABELS = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const displayDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const requestErrorMessage = error => (
  error?.response?.data?.error
  || error?.response?.data?.message
  || error?.message
  || 'The registry review queue could not be loaded.'
);

function useRegistryQueue({ enabled, loader, tenantSlug, status, page, limit, refreshKey }) {
  const sequence = useRef(0);
  const [state, setState] = useState({
    rows: [], page: 1, limit, total: 0, totalPages: 1, loading: enabled, error: '',
  });

  useEffect(() => {
    if (!enabled) {
      setState(current => ({ ...current, rows: [], total: 0, totalPages: 1, loading: false, error: '' }));
      return undefined;
    }
    const requestId = ++sequence.current;
    setState(current => ({ ...current, loading: true, error: '' }));
    loader(tenantSlug, { page, limit, status: status || undefined })
      .then(response => {
        if (requestId !== sequence.current) return;
        setState({
          ...normalizeRegistryQueuePage(response.data, { page, limit }),
          loading: false,
          error: '',
        });
      })
      .catch(error => {
        if (requestId !== sequence.current) return;
        setState(current => ({
          ...current, rows: [], total: 0, totalPages: 1, loading: false, error: requestErrorMessage(error),
        }));
      });
    return () => { sequence.current += 1; };
  }, [enabled, limit, loader, page, refreshKey, status, tenantSlug]);

  return state;
}

function RegistryRequestCard({ request, resourceType, tenantSlug, user, canVerify }) {
  const ownProposal = isOwnRegistryProposal(request, user);
  const pending = request.status === 'pending';
  const baselineCount = Array.isArray(request.baseline_set) ? request.baseline_set.length : 0;
  const requestedCount = Array.isArray(request.requested_set) ? request.requested_set.length : 0;
  const reviewPath = buildRegistryReviewPath({
    tenantSlug,
    resourceType,
    resourceUid: request.resource_uid,
  });
  const subjectLabel = resourceType === 'business' ? 'Business' : 'Property';
  const setLabel = resourceType === 'business' ? 'party roles' : 'interests';

  return (
    <article className={`registry-review-row status-${request.status}`}>
      <div className="registry-review-subject">
        <span>{subjectLabel}</span>
        <strong title={request.resource_uid}>{compactRegistryUid(request.resource_uid)}</strong>
        <small>{baselineCount} → {requestedCount} {setLabel}</small>
      </div>
      <div className="registry-review-request">
        <span>Request</span>
        <strong title={request.request_uid}>{compactRegistryUid(request.request_uid)}</strong>
        <small>Submitted {displayDateTime(request.proposed_at)}</small>
      </div>
      <div className="registry-review-maker">
        <span>Proposer / Maker</span>
        <strong>User #{request.proposed_by_user_id}</strong>
        <small>{ownProposal ? 'Your proposal — independent review required' : 'Independent staff proposal'}</small>
      </div>
      <div className="registry-review-version">
        <span>Versions</span>
        <strong>Registry v{request.base_resource_version}</strong>
        <small>Request v{request.version}</small>
      </div>
      <div className="registry-review-reason">
        <span>Proposal reason</span>
        <p>{request.proposal_reason || '—'}</p>
        {request.review_reason && <small>Review: {request.review_reason}</small>}
      </div>
      <div className="registry-review-decision">
        <span className={`registry-review-status ${request.status}`}>{STATUS_LABELS[request.status] || request.status}</span>
        {request.reviewed_by_user_id && <small>Checker User #{request.reviewed_by_user_id}<br />{displayDateTime(request.reviewed_at)}</small>}
        <Link className={pending && canVerify && !ownProposal ? 'review' : ''} to={reviewPath}>
          {pending && canVerify && !ownProposal ? 'Open comparison & review' : 'Open record & history'} →
        </Link>
      </div>
    </article>
  );
}

function QueuePagination({ state, page, onPage }) {
  const first = state.total ? ((page - 1) * state.limit) + 1 : 0;
  const last = Math.min(page * state.limit, state.total);
  return (
    <footer className="registry-review-pagination">
      <span>Showing {first}–{last} of {state.total}</span>
      <div>
        <button type="button" onClick={() => onPage(page - 1)} disabled={state.loading || page <= 1}>← Previous</button>
        <span>Page {page} of {state.totalPages}</span>
        <button type="button" onClick={() => onPage(page + 1)} disabled={state.loading || page >= state.totalPages}>Next →</button>
      </div>
    </footer>
  );
}

function RegistryQueueSection({
  resourceType, title, description, state, page, onPage, tenantSlug, user, canView, canVerify, onRetry,
}) {
  return (
    <section className="registry-review-section" aria-labelledby={`${resourceType}-queue-title`}>
      <header>
        <div>
          <span className={`registry-review-domain ${resourceType}`}>{resourceType === 'business' ? 'व्यवसाय' : 'मालमत्ता'}</span>
          <h2 id={`${resourceType}-queue-title`}>{title}</h2>
          <p>{description}</p>
        </div>
        {canView && <strong className="registry-review-total">{state.total}<small>matching requests</small></strong>}
      </header>

      {!canView && canVerify && (
        <div className="registry-review-notice warning" role="alert">
          <strong>View permission is also required.</strong>
          <p>Your role can verify this registry, but the queue endpoint requires its <code>view</code> permission before records may be disclosed.</p>
        </div>
      )}
      {canView && state.error && (
        <div className="registry-review-notice error" role="alert">
          <div><strong>Could not load this queue.</strong><p>{state.error}</p></div>
          <button type="button" onClick={onRetry}>Retry</button>
        </div>
      )}
      {canView && state.loading && <div className="registry-review-loading" role="status">Loading review requests…</div>}
      {canView && !state.loading && !state.error && state.rows.length === 0 && (
        <div className="registry-review-empty">
          <strong>No matching requests</strong>
          <p>There are no registry changes in the selected status for this module.</p>
        </div>
      )}
      {canView && !state.loading && !state.error && state.rows.length > 0 && (
        <div className="registry-review-list">
          <div className="registry-review-column-headings" aria-hidden="true">
            <span>Subject</span><span>Request</span><span>Proposer</span><span>Version</span><span>Reason</span><span>Status & action</span>
          </div>
          {state.rows.map(request => (
            <RegistryRequestCard
              key={request.request_uid}
              request={request}
              resourceType={resourceType}
              tenantSlug={tenantSlug}
              user={user}
              canVerify={canVerify}
            />
          ))}
          <QueuePagination state={state} page={page} onPage={onPage} />
        </div>
      )}
    </section>
  );
}

export default function AdminRegistryReviewQueue() {
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const canOpen = canOpenRegistryReviewQueue(user);
  const businessView = canViewRegistryQueueDomain(user, 'businesses');
  const businessVerify = canVerifyRegistryQueueDomain(user, 'businesses');
  const propertyView = canViewRegistryQueueDomain(user, 'properties');
  const propertyVerify = canVerifyRegistryQueueDomain(user, 'properties');
  const [status, setStatus] = useState('pending');
  const [limit, setLimit] = useState(20);
  const [businessPage, setBusinessPage] = useState(1);
  const [propertyPage, setPropertyPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const businesses = useRegistryQueue({
    enabled: businessView,
    loader: registryChangeRequestAPI.getBusinessQueue,
    tenantSlug,
    status,
    page: businessPage,
    limit,
    refreshKey,
  });
  const properties = useRegistryQueue({
    enabled: propertyView,
    loader: registryChangeRequestAPI.getPropertyQueue,
    tenantSlug,
    status,
    page: propertyPage,
    limit,
    refreshKey,
  });

  const updateStatus = event => {
    setStatus(event.target.value);
    setBusinessPage(1);
    setPropertyPage(1);
  };
  const updateLimit = event => {
    setLimit(Number(event.target.value));
    setBusinessPage(1);
    setPropertyPage(1);
  };

  if (!canOpen) {
    return <section className="registry-review-denied" role="alert"><h1>Registry change reviews</h1><p>You do not have permission to view or verify business/property registry changes.</p></section>;
  }

  return (
    <div className="registry-review-page">
      <header className="registry-review-hero">
        <div>
          <span>Independent registry control / स्वतंत्र नोंद पडताळणी</span>
          <h1>Registry change review inbox</h1>
          <p>Find complete business party-role and property-interest replacement requests. Decisions remain on the subject workspace so the checker sees the immutable before/after evidence.</p>
        </div>
        <div className="registry-review-hero-facts">
          <div><strong>{Number(businessView) + Number(propertyView)}</strong><span>registries readable</span></div>
          <div><strong>{(businesses.total || 0) + (properties.total || 0)}</strong><span>matching requests</span></div>
        </div>
      </header>

      <div className="registry-review-controls" aria-label="Registry review filters">
        <label>Status
          <select value={status} onChange={updateStatus}>
            {STATUS_OPTIONS.map(option => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label>Per registry
          <select value={limit} onChange={updateLimit}>
            <option value="10">10 per page</option><option value="20">20 per page</option><option value="50">50 per page</option>
          </select>
        </label>
        <button type="button" onClick={() => setRefreshKey(value => value + 1)} disabled={businesses.loading || properties.loading}>Refresh queues</button>
        <p><strong>Maker–checker:</strong> proposers cannot decide their own requests.</p>
      </div>

      {(businessView || businessVerify) && (
        <RegistryQueueSection
          resourceType="business"
          title="Business party-role changes"
          description="Ownership, liable-party and representative sets for verified businesses."
          state={businesses}
          page={businessPage}
          onPage={setBusinessPage}
          tenantSlug={tenantSlug}
          user={user}
          canView={businessView}
          canVerify={businessVerify}
          onRetry={() => setRefreshKey(value => value + 1)}
        />
      )}
      {(propertyView || propertyVerify) && (
        <RegistryQueueSection
          resourceType="property"
          title="Property-interest changes"
          description="Complete ownership and liable-interest replacements for verified properties."
          state={properties}
          page={propertyPage}
          onPage={setPropertyPage}
          tenantSlug={tenantSlug}
          user={user}
          canView={propertyView}
          canVerify={propertyVerify}
          onRetry={() => setRefreshKey(value => value + 1)}
        />
      )}
    </div>
  );
}
